import React, { useState, useEffect } from "react";
import "./modelByModelSearch.css";
import ScrollableComponent from "../ScrollableComponent";
import { fetchWithAuth } from "../../utils/fetchWithAuth";
import {
  hasDbEditorUpdateAccessFromStorage,
} from "../../utils/authAccess";
import ViewListIcon from '@mui/icons-material/ViewList';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import CategoryIcon from '@mui/icons-material/Category';
import AddNewModelModal, {
  AddNewModelSuccessPopup,
} from "../addNewModelModal/AddNewModelModal";

export default function ModelByModelSearch({
  data = [],
  documentData = null,
  partByModelData = null,
  imageData = null,
  setSelectedTab,
  setDocumentData,
  setPartByModelData,
  setPartData,
  setImageData,
  setModelData,
}) {
  const [filteredData, setFilteredData] = useState([]);
  const [searchModel, setSearchModel] = useState("");
  const [searchBrand, setSearchBrand] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [isSubAssemblesModalOpen, setIsSubAssemblesModalOpen] = useState(false);
  const [subAssemblesData, setSubAssemblesData] = useState([]);
  const [subAssemblesLoading, setSubAssemblesLoading] = useState(false);
  const [subAssemblesError, setSubAssemblesError] = useState("");
  const [selectedSubAssemblies, setSelectedSubAssemblies] = useState([]);
  const [selectedModelForSubAssemblies, setSelectedModelForSubAssemblies] = useState(null);
  const [deleteSubAssembliesLoading, setDeleteSubAssembliesLoading] = useState(false);
  const [isAddSubassemblyModalOpen, setIsAddSubassemblyModalOpen] = useState(false);
  const [addSubassemblySubModelId, setAddSubassemblySubModelId] = useState("");
  const [addSubassemblyIsEngineModel, setAddSubassemblyIsEngineModel] = useState(false);
  const [addSubassemblyLoading, setAddSubassemblyLoading] = useState(false);
  const [addSubassemblyError, setAddSubassemblyError] = useState("");
  const [isDeleteConfirmModalOpen, setIsDeleteConfirmModalOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [isUpdateDescriptionModalOpen, setIsUpdateDescriptionModalOpen] = useState(false);
  const [updateDescriptionName, setUpdateDescriptionName] = useState("");
  const [updateDescriptionLoading, setUpdateDescriptionLoading] = useState(false);
  const [updateDescriptionError, setUpdateDescriptionError] = useState("");
  const [isUpdateProductModalOpen, setIsUpdateProductModalOpen] = useState(false);
  const [productTypes, setProductTypes] = useState([]);
  const [selectedProductTypeId, setSelectedProductTypeId] = useState("");
  const [selectedProductTypeName, setSelectedProductTypeName] = useState("");
  const [originalProductTypeId, setOriginalProductTypeId] = useState("");
  const [updateProductLoading, setUpdateProductLoading] = useState(false);
  const [updateProductError, setUpdateProductError] = useState("");
  const [productTypesLoading, setProductTypesLoading] = useState(false);
  const [isAddNewModelModalOpen, setIsAddNewModelModalOpen] = useState(false);
  const [addNewModelSuccessMessage, setAddNewModelSuccessMessage] =
    useState("");

  const username = localStorage.getItem("username") || "DBEditorWeb";
  const isAdmin = hasDbEditorUpdateAccessFromStorage();

  useEffect(() => {
    setFilteredData(data);
  }, [data]);

  useEffect(() => {
    if (!addNewModelSuccessMessage) return undefined;
    const id = setTimeout(() => setAddNewModelSuccessMessage(""), 5000);
    return () => clearTimeout(id);
  }, [addNewModelSuccessMessage]);

  const handleModelSearch = (event) => {
    const model = event.target.value;
    setSearchModel(model);
    setFilteredData(
      data.filter((item) =>
        item.number.toLowerCase().includes(model.toLowerCase())
      )
    );
  };

  const handleBrandSearch = (event) => {
    const brand = event.target.value;
    setSearchBrand(brand);
    setFilteredData(
      data.filter((item) =>
        item.brandName.toLowerCase().includes(brand.toLowerCase())
      )
    );
  };

  const handleClick = async (row) => {
    const hsin = row.hsin;
    const model = row.number;
    setLoading(true);
    setError("");

    try {
      const docData = await fetchWithAuth({
        method: "GET",
        endpoint: `/model/getDocumentByModelNumber`,
        params: { hsin: hsin },
      });

      console.log("Doc data:", docData);

      // Check if no documents found
      if (!docData || !Array.isArray(docData) || docData.length === 0) {
        setError("No Document Found.");
        return;
      }

      if (docData.length > 1) {
        setDocumentData(docData);
        setSelectedTab("DOCUMENTS");
        setPartByModelData([]);
        setPartData([]);
        setImageData([]);
      } else if (docData.length === 1) {
        const partData = await fetchWithAuth({
          method: "GET",
          endpoint: `/model/getPartAndPagesByModel`,
          params: { modelNumber: model, documentId: docData[0].documentId },
        });
        console.log("Part data:", partData);

        let enrichedPartData = partData;
        if (Array.isArray(partData) && partData.length > 0) {
          enrichedPartData = [...partData];
          if (enrichedPartData[0]) {
            enrichedPartData[0] = {
              ...enrichedPartData[0],
              model: row.number || model || "",
              brndName: row.brandName || "",
              prdName: row.productType || "",
              documentId: docData[0].documentId, // Store documentId in the data for later use
            };
          }
        }

        if (Array.isArray(enrichedPartData) && enrichedPartData.length > 0 && 
          enrichedPartData.some(item => item.partNumber != null || item.number != null)) {

          setImageData(enrichedPartData);
          setSelectedTab("PARTS");
          setPartByModelData(enrichedPartData);
          setPartData([]);
          setDocumentData(docData);
        } else {      
          setImageData(enrichedPartData);
          setSelectedTab("IMAGE");
          setPartByModelData([]);
          setPartData([]);
          setDocumentData(docData);
        }
      }
    } catch (err) {
      console.error("Error fetching data:", err);
      setError("Error fetching data.");
    } finally {
      setLoading(false);
    }
  };

  const closeErrorPopup = () => {
    setError("");
    setSelectedTab("MODELS");
  };

  const handleRowClick = (row, index) => {
    const isSelected = selectedRows.some(selectedRow => selectedRow.index === index);

    if (isSelected) {
      setSelectedRows(selectedRows.filter(selectedRow => selectedRow.index !== index));
    } else {
      setSelectedRows([...selectedRows, { ...row, index }]);
    }
  };

  const handleSelectAllRows = () => {
    if (selectedRows.length === filteredData.length) {
      setSelectedRows([]);
    } else {
      const allRows = filteredData.map((row, index) => ({ ...row, index }));
      setSelectedRows(allRows);
    }
  };

  const handleViewSubAssemblies = async () => {
    if (!isAdmin) {
      return;
    }
    if (selectedRows.length === 0) {
      return;
    }

    const selectedModel = selectedRows[0];
    const hsin = selectedModel.hsin;

    if (!hsin) {
      setSubAssemblesError("No HSIN found for selected model.");
      setIsSubAssemblesModalOpen(true);
      return;
    }

    setIsSubAssemblesModalOpen(true);
    setSubAssemblesLoading(true);
    setSubAssemblesError("");
    setSubAssemblesData([]);
    setSelectedSubAssemblies([]);
    setSelectedModelForSubAssemblies(selectedModel);

    try {
      const response = await fetchWithAuth({
        method: "GET",
        endpoint: `/model/viewSubassemblies`,
        params: { hsin: hsin },
      });

      if (response && Array.isArray(response)) {
        setSubAssemblesData(response);
      } else {
        setSubAssemblesData([]);
      }
    } catch (err) {
      console.error("Error fetching sub-assemblies:", err);
      setSubAssemblesError("Error fetching sub-assemblies.");
      setSubAssemblesData([]);
    } finally {
      setSubAssemblesLoading(false);
    }
  };

  const handleCloseSubAssemblesModal = () => {
    setIsSubAssemblesModalOpen(false);
    setSubAssemblesData([]);
    setSubAssemblesError("");
    setSelectedSubAssemblies([]);
    setSelectedModelForSubAssemblies(null);
  };

  const handleSubAssemblyRowClick = (item, index) => {
    const isSelected = selectedSubAssemblies.some(selected => selected.index === index);

    if (isSelected) {
      setSelectedSubAssemblies(selectedSubAssemblies.filter(selected => selected.index !== index));
    } else {
      setSelectedSubAssemblies([...selectedSubAssemblies, { ...item, index }]);
    }
  };

  const handleDeleteSubAssemblies = async () => {
    if (selectedSubAssemblies.length === 0 || !selectedModelForSubAssemblies) {
      return;
    }

    setDeleteSubAssembliesLoading(true);
    setSubAssemblesError("");

    try {
      const deletePayload = selectedSubAssemblies.map((item) => ({
        modelId: selectedModelForSubAssemblies.id || "",
        hsin: selectedModelForSubAssemblies.hsin || "",
        subModelId: item.subModelId || "",
        subHsin: item.subHsin || item.hsin || "",
        isEngineModel: item.isEngineModel || false,
      }));

      await fetchWithAuth({
        method: "PUT",
        endpoint: `/model/deleteSubassemblies`,
        params: { username: username },
        headers: { "Content-Type": "application/json" },
        data: deletePayload,
      });

      const hsin = selectedModelForSubAssemblies.hsin;
      if (hsin) {
        setSubAssemblesLoading(true);
        const response = await fetchWithAuth({
          method: "GET",
          endpoint: `/model/viewSubassemblies`,
          params: { hsin: hsin },
        });

        if (response && Array.isArray(response)) {
          setSubAssemblesData(response);
        } else {
          setSubAssemblesData([]);
        }
        setSubAssemblesLoading(false);
      }

      setSelectedSubAssemblies([]);
    } catch (err) {
      console.error("Error deleting sub-assemblies:", err);
      
      let errorMessage = "Failed to delete sub-assemblies. Please try again.";
      
      if (err.response?.data) {
        if (typeof err.response.data === 'string') {
          errorMessage = err.response.data;
        } else if (err.response.data.message) {
          errorMessage = err.response.data.message;
        } else if (err.response.data.error) {
          errorMessage = err.response.data.error;
        } else {
          errorMessage = JSON.stringify(err.response.data);
        }
      } else {
        errorMessage = err.message || errorMessage;
      }
      
      setSubAssemblesError(errorMessage);
    } finally {
      setDeleteSubAssembliesLoading(false);
    }
  };

  const handleOpenAddSubassemblyModal = () => {
    if (!isAdmin) {
      return;
    }
    if (selectedRows.length !== 1) {
      return;
    }
    setIsAddSubassemblyModalOpen(true);
    setAddSubassemblySubModelId("");
    setAddSubassemblyIsEngineModel(false);
    setAddSubassemblyError("");
  };

  const handleCloseAddSubassemblyModal = () => {
    setIsAddSubassemblyModalOpen(false);
    setAddSubassemblySubModelId("");
    setAddSubassemblyIsEngineModel(false);
    setAddSubassemblyError("");
  };

  const handleAddSubassembly = async () => {
    if (selectedRows.length !== 1) {
      setAddSubassemblyError("Please select a model.");
      return;
    }

    if (!addSubassemblySubModelId.trim()) {
      setAddSubassemblyError("Please enter Sub Model ID.");
      return;
    }

    const selectedModel = selectedRows[0];
    const modelId = selectedModel.id;

    if (!modelId) {
      setAddSubassemblyError("No Model ID found for selected model.");
      return;
    }

    setAddSubassemblyLoading(true);
    setAddSubassemblyError("");

    try {
      const payload = {
        modelId: modelId.trim(),
        subModelId: addSubassemblySubModelId.trim(),
        isEngineModel: addSubassemblyIsEngineModel ? 1 : 0,
      };

      await fetchWithAuth({
        method: "PUT",
        endpoint: `/model/addSubassembly`,
        params: { username: username },
        headers: { "Content-Type": "application/json" },
        data: payload,
      });

      handleCloseAddSubassemblyModal();
    } catch (err) {
      console.error("Error adding subassembly:", err);
      
      let errorMessage = "Failed to add subassembly. Please try again.";
      
      if (err.response?.data) {
        if (typeof err.response.data === 'string') {
          errorMessage = err.response.data;
        } else if (err.response.data.message) {
          errorMessage = err.response.data.message;
        } else if (err.response.data.error) {
          errorMessage = err.response.data.error;
        } else {
          errorMessage = JSON.stringify(err.response.data);
        }
      } else {
        errorMessage = err.message || errorMessage;
      }
      
      setAddSubassemblyError(errorMessage);
    } finally {
      setAddSubassemblyLoading(false);
    }
  };

  const handleDeleteModel = () => {
    if (!isAdmin) {
      return;
    }
    if (selectedRows.length === 0) {
      return;
    }
    setIsDeleteConfirmModalOpen(true);
    setError("");
  };

  const handleCancelDelete = () => {
    setIsDeleteConfirmModalOpen(false);
    setError("");
  };

  const handleConfirmDelete = async () => {
    if (selectedRows.length === 0) {
      return;
    }

    setDeleteLoading(true);
    setError("");

    try {
      const deletePayload = selectedRows.map((row) => ({
        hsin: row.hsin || "",
        id: row.id || "",
        number: row.number || "",
        formattedNumber: row.formattedNumber || row.number || "",
        brandName: row.brandName || "",
        taxonomyName: row.taxonomyName || row.productType || "",
        name: row.name || "",
      }));

      await fetchWithAuth({
        method: "PUT",
        endpoint: `/model/deleteModel`,
        params: { username: username },
        headers: { "Content-Type": "application/json" },
        data: deletePayload,
      });

      const deletedHsins = selectedRows.map(row => row.hsin).filter(Boolean);
      const updatedModelData = data.filter(model => !deletedHsins.includes(model.hsin));
      
      if (setModelData) {
        setModelData(updatedModelData);
      }
      
      setIsDeleteConfirmModalOpen(false);
      setSelectedRows([]);
      setError("");
    } catch (err) {
      console.error("Error deleting models:", err);
      
      let errorMessage = "Failed to delete model(s). Please try again.";
      
      if (err.response?.status === 400) {
        if (err.response?.data) {
          if (typeof err.response.data === 'string') {
            errorMessage = err.response.data;
          } else if (err.response.data.message) {
            errorMessage = err.response.data.message;
          } else if (err.response.data.error) {
            errorMessage = err.response.data.error;
          } else {
            errorMessage = JSON.stringify(err.response.data);
          }
        }
      } else {
        errorMessage = err.response?.data?.message || 
                      err.response?.data?.error || 
                      err.message || 
                      errorMessage;
      }
      
      setError(errorMessage);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleOpenUpdateDescriptionModal = () => {
    if (!isAdmin) {
      return;
    }
    if (selectedRows.length !== 1) {
      return;
    }
    const selectedModel = selectedRows[0];
    setIsUpdateDescriptionModalOpen(true);
    setUpdateDescriptionName(selectedModel.name || "");
    setUpdateDescriptionError("");
  };

  const handleCloseUpdateDescriptionModal = () => {
    setIsUpdateDescriptionModalOpen(false);
    setUpdateDescriptionName("");
    setUpdateDescriptionError("");
  };

  const handleUpdateDescription = async () => {
    if (selectedRows.length !== 1) {
      setUpdateDescriptionError("Please select a model.");
      return;
    }

    if (!updateDescriptionName.trim()) {
      setUpdateDescriptionError("Please enter a description.");
      return;
    }

    const selectedModel = selectedRows[0];
    const hsin = selectedModel.hsin;

    if (!hsin) {
      setUpdateDescriptionError("No HSIN found for selected model.");
      return;
    }

    setUpdateDescriptionLoading(true);
    setUpdateDescriptionError("");

    try {
      const payload = {
        hsin: hsin.trim(),
        name: updateDescriptionName.trim(),
      };

      await fetchWithAuth({
        method: "PUT",
        endpoint: `/model/updateModelName`,
        params: { username: username },
        headers: { "Content-Type": "application/json" },
        data: payload,
      });

      if (setModelData && data) {
        const updatedModelData = data.map(model => 
          model.hsin === hsin 
            ? { ...model, name: updateDescriptionName.trim() }
            : model
        );
        setModelData(updatedModelData);
      }

      handleCloseUpdateDescriptionModal();
      setSelectedRows([]);
    } catch (err) {
      console.error("Error updating description:", err);
      
      let errorMessage = "Failed to update description. Please try again.";
      
      if (err.response?.status === 400) {
        if (err.response?.data) {
          if (typeof err.response.data === 'string') {
            errorMessage = err.response.data;
          } else if (err.response.data.message) {
            errorMessage = err.response.data.message;
          } else if (err.response.data.error) {
            errorMessage = err.response.data.error;
          } else {
            errorMessage = JSON.stringify(err.response.data);
          }
        }
      } else {
        errorMessage = err.response?.data?.message || 
                      err.response?.data?.error || 
                      err.message || 
                      errorMessage;
      }
      
      setUpdateDescriptionError(errorMessage);
    } finally {
      setUpdateDescriptionLoading(false);
    }
  };

  const fetchProductTypes = async () => {
    setProductTypesLoading(true);
    try {
      const productTypesData = await fetchWithAuth({
        method: "GET",
        endpoint: "/taxonomy/allProductTypes",
      });
      const fetchedProductTypes = Array.isArray(productTypesData) ? productTypesData : [];
      setProductTypes(fetchedProductTypes);
      return fetchedProductTypes;
    } catch (err) {
      console.error("Error fetching product types:", err);
      setProductTypes([]);
      setUpdateProductError("Failed to load product types.");
      return [];
    } finally {
      setProductTypesLoading(false);
    }
  };

  const refreshModelListAfterAdd = async (modelNumber) => {
    if (!setModelData || !modelNumber) return;
    const savedSearchValue = sessionStorage.getItem("searchValue");
    const searchValue = savedSearchValue ? JSON.parse(savedSearchValue) : "";
    const searchTerm =
      searchValue && typeof searchValue === "string" ? searchValue.trim() : "";
    const lookupNumber = searchTerm.length >= 3 ? searchTerm : modelNumber;

    try {
      const modelDataResponse = await fetchWithAuth({
        method: "GET",
        endpoint: "/model/getModelByNumber",
        params: { number: lookupNumber },
      });
      if (modelDataResponse && Array.isArray(modelDataResponse)) {
        setModelData(modelDataResponse);
      }
    } catch (refetchErr) {
      console.error("Error refetching models after add:", refetchErr);
    }
  };

  const handleOpenAddNewModelModal = () => {
    if (!isAdmin) return;
    setIsAddNewModelModalOpen(true);
  };

  const handleOpenUpdateProductModal = async () => {
    if (!isAdmin) {
      return;
    }
    if (selectedRows.length !== 1) {
      return;
    }
    setIsUpdateProductModalOpen(true);
    setSelectedProductTypeId("");
    setSelectedProductTypeName("");
    setOriginalProductTypeId("");
    setUpdateProductError("");
    
    // Extract first 7 characters from model id
    const selectedModel = selectedRows[0];
    const modelId = selectedModel.id || "";
    const currentProductTypeId = modelId.length >= 7 ? modelId.substring(0, 7) : "";
    
    const fetchedProductTypes = await fetchProductTypes();
    
    if (currentProductTypeId && fetchedProductTypes.length > 0) {
      const currentProductType = fetchedProductTypes.find(pt => {
        const ptId = pt.id?.toString() || "";
        return ptId === currentProductTypeId;
      });
      if (currentProductType) {
        const matchingId = currentProductType.id?.toString() || "";
        setSelectedProductTypeId(matchingId);
        setSelectedProductTypeName(currentProductType.name || "");
        setOriginalProductTypeId(matchingId);
      }
    } else {
      setOriginalProductTypeId("");
    }
  };

  const handleCloseUpdateProductModal = () => {
    setIsUpdateProductModalOpen(false);
    setSelectedProductTypeId("");
    setSelectedProductTypeName("");
    setOriginalProductTypeId("");
    setUpdateProductError("");
  };

  const handleProductTypeChange = (e) => {
    const productTypeId = e.target.value;
    setSelectedProductTypeId(productTypeId);
    
    const selectedProductType = productTypes.find(pt => 
      pt.id === productTypeId || pt.id?.toString() === productTypeId
    );
    setSelectedProductTypeName(selectedProductType?.name || "");
  };

  const handleUpdateProduct = async () => {
    if (selectedRows.length !== 1) {
      setUpdateProductError("Please select a model.");
      return;
    }

    if (!selectedProductTypeId) {
      setUpdateProductError("Please select a product type.");
      return;
    }

    const selectedModel = selectedRows[0];
    const hsin = selectedModel.hsin;
    const modelId = selectedModel.id || "";

    if (!hsin) {
      setUpdateProductError("No HSIN found for selected model.");
      return;
    }

    if (!modelId || modelId.length < 7) {
      setUpdateProductError("Invalid model ID.");
      return;
    }

    const newModelId = selectedProductTypeId + modelId.substring(7);

    setUpdateProductLoading(true);
    setUpdateProductError("");

    try {
      const payload = {
        hsin: hsin,
        modelId: modelId,
        newModelId: newModelId,
      };

      await fetchWithAuth({
        method: "PUT",
        endpoint: `/model/updateProductForModel`,
        params: { username: username },
        headers: { "Content-Type": "application/json" },
        data: payload,
      });

      handleCloseUpdateProductModal();
      setSelectedRows([]);

      // Refetch model list and any loaded document/parts/image data so the UI stays in sync
      const savedSearchValue = sessionStorage.getItem("searchValue");
      const searchValue = savedSearchValue ? JSON.parse(savedSearchValue) : "";
      const searchTerm = searchValue && typeof searchValue === "string" ? searchValue.trim() : "";
      const hasSearchTerm = searchTerm.length >= 3;

      if (hasSearchTerm && setModelData) {
        try {
          const modelData = await fetchWithAuth({
            method: "GET",
            endpoint: "/model/getModelByNumber",
            params: { number: searchTerm },
          });
          if (modelData && Array.isArray(modelData)) {
            setModelData(modelData);
          }

          const hasDocumentData = documentData && Array.isArray(documentData) && documentData.length > 0;
          const hasPartsOrImageData = (partByModelData && partByModelData.length > 0) || (imageData && imageData.length > 0);

          let docData = null;
          if (hasDocumentData && setDocumentData) {
            const docResponse = await fetchWithAuth({
              method: "GET",
              endpoint: "/model/getDocumentByModelNumber",
              params: { hsin },
            });
            if (docResponse && Array.isArray(docResponse) && docResponse.length > 0) {
              docData = docResponse;
              setDocumentData(docData);
            }
          }

          if ((hasPartsOrImageData || docData) && (setPartByModelData || setImageData)) {
            const documentId = docData?.[0]?.documentId ?? documentData?.[0]?.documentId ?? partByModelData?.[0]?.documentId;
            if (documentId) {
              const partData = await fetchWithAuth({
                method: "GET",
                endpoint: "/model/getPartAndPagesByModel",
                params: { modelNumber: searchTerm, documentId },
              });
              let enrichedPartData = partData;
              if (Array.isArray(partData) && partData.length > 0) {
                const modelInfo = (modelData && modelData[0]) || selectedModel;
                enrichedPartData = [...partData];
                if (enrichedPartData[0]) {
                  enrichedPartData[0] = {
                    ...enrichedPartData[0],
                    model: modelInfo?.number || modelInfo?.modelNumber || modelInfo?.model || searchTerm,
                    brndName: modelInfo?.brandName || modelInfo?.brndName || "",
                    prdName: modelInfo?.productType || modelInfo?.productName || modelInfo?.prdName || "",
                    documentId,
                  };
                }
              }
              if (enrichedPartData && Array.isArray(enrichedPartData)) {
                if (setPartByModelData) setPartByModelData(enrichedPartData);
                if (setImageData) setImageData(enrichedPartData);
              }
            }
          }
        } catch (refetchErr) {
          console.error("Error refetching data after product update:", refetchErr);
        }
      }
    } catch (err) {
      console.error("Error updating product:", err);
      
      let errorMessage = "Failed to update product. Please try again.";
      
      if (err.response?.data) {
        if (typeof err.response.data === 'string') {
          errorMessage = err.response.data;
        } else if (err.response.data.message) {
          errorMessage = err.response.data.message;
        } else if (err.response.data.error) {
          errorMessage = err.response.data.error;
        } else {
          errorMessage = JSON.stringify(err.response.data);
        }
      } else {
        errorMessage = err.message || errorMessage;
      }
      
      setUpdateProductError(errorMessage);
    } finally {
      setUpdateProductLoading(false);
    }
  };

  return (
    <div className="mainPageContent">
      <ScrollableComponent height="500px">
        <table className="part" style={{ width: "100%" }}>
          <thead>
            <tr>
              <th>
                <input
                  type="checkbox"
                  checked={selectedRows.length === filteredData.length && filteredData.length > 0}
                  onChange={handleSelectAllRows}
                />
              </th>
              <th>Model</th>
              <th>Brand</th>
              <th>Product Type</th>
              <th>Description</th>
            </tr>
            <tr>
              <td />
              <td>
                <input
                  type="text"
                  placeholder="Search Model"
                  className="modelSearchInput"
                  value={searchModel}
                  onChange={handleModelSearch}
                />
              </td>
              <td>
                <input
                  type="text"
                  placeholder="Search Brand"
                  className="modelSearchInput"
                  value={searchBrand}
                  onChange={handleBrandSearch}
                />
              </td>
              <td></td>
              <td></td>
            </tr>
          </thead>
          <tbody>
            {filteredData &&
              filteredData.length !== 0 &&
              filteredData.map((row, index) => (
                <tr key={index}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedRows.some(selectedRow => selectedRow.index === index)}
                      onChange={() => handleRowClick(row, index)}
                    />
                  </td>
                  <td
                    className="clickable-text"
                    onClick={() => handleClick(row)}
                  >
                    <b>{row.number}</b>
                  </td>
                  <td>{row.brandName}</td>
                  <td>{row.taxonomyName}</td>
                  <td>{row.name}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </ScrollableComponent>

      {isAdmin && (
        <div className="buttonPartGroup">
          <table className="buttonTable" style={{ width: "100%" }}>
            <thead>
              <tr>
                {isAdmin && (
                <td>
                  <button 
                    className="btn btn-info" 
                    disabled={selectedRows.length !== 1} 
                    onClick={handleViewSubAssemblies}
                  >
                    <ViewListIcon /> View/Remove SubAssemblies
                  </button>
                </td>
                )}
                {isAdmin && (
                <td>
                  <button 
                    className="btn btn-info" 
                    disabled={selectedRows.length !== 1}
                    onClick={handleOpenAddSubassemblyModal}
                  >
                    <AddIcon /> Add SubAssemblies
                  </button>
                </td>
                )}
                {isAdmin && (
                <td>
                  <button
                    className="btn btn-info"
                    onClick={handleOpenAddNewModelModal}
                  >
                    <AddIcon /> Add New Model
                  </button>
                </td>
                )}
                {isAdmin && (
                <td>
                  <button 
                    className="btn btn-info" 
                    disabled={selectedRows.length === 0}
                    onClick={handleDeleteModel}
                  >
                    <DeleteIcon /> Delete Model(s)
                  </button>
                </td>
                )}
                {isAdmin && (
                <td>
                  <button 
                    className="btn btn-info" 
                    disabled={selectedRows.length !== 1}
                    onClick={handleOpenUpdateDescriptionModal}
                  >
                    <EditIcon /> Update Description
                  </button>
                </td>
                )}
                {isAdmin && (
                <td>
                  <button 
                    className="btn btn-info" 
                    disabled={selectedRows.length !== 1}
                    onClick={handleOpenUpdateProductModal}
                  >
                    <CategoryIcon /> Update Product
                  </button>
                </td>
                )}
              </tr>
            </thead>
          </table>
        </div>
      )}

      {loading && (
        <>
          <div className="blur-background"></div>
          <div className="loading"></div>
        </>
      )}

      {error && !isDeleteConfirmModalOpen && !isAddSubassemblyModalOpen && !isUpdateDescriptionModalOpen && !isAddNewModelModalOpen && <div className="blur-overlay"></div>}

      {error && !isDeleteConfirmModalOpen && !isAddSubassemblyModalOpen && !isUpdateDescriptionModalOpen && !isAddNewModelModalOpen && (
        <div className="popup">
          <div className="popup-message">{error}</div>
          <button className="popup-close" onClick={closeErrorPopup}>
            &times;
          </button>
        </div>
      )}

      {addNewModelSuccessMessage ? (
        <AddNewModelSuccessPopup
          message={addNewModelSuccessMessage}
          onDismiss={() => setAddNewModelSuccessMessage("")}
        />
      ) : null}

      {isSubAssemblesModalOpen && (
        <div className="modalOverlay">
          <div className="modalContent" style={{ minWidth: "600px", maxWidth: "800px" }}>
            <h3>Sub-Assemblies</h3>
            
            {subAssemblesLoading && (
              <div style={{ textAlign: "center", padding: "20px" }}>
                <div className="button-spinner" style={{ margin: "0 auto" }}></div>
                <p>Loading sub-assemblies...</p>
              </div>
            )}

            {!subAssemblesLoading && subAssemblesError && (
              <div style={{ textAlign: "center", padding: "20px", color: "red" }}>
                <p>{subAssemblesError}</p>
              </div>
            )}

            {!subAssemblesLoading && !subAssemblesError && (
              <>
                {subAssemblesData.length === 0 ? (
                  <div style={{ textAlign: "center", padding: "20px" }}>
                    <p>No sub-assemblies found.</p>
                  </div>
                ) : (
                  <div style={{ maxHeight: "500px", overflowY: "auto", marginBottom: "16px" }}>
                    <table className="part" style={{ width: "100%" }}>
                      <thead>
                        <tr>
                          <th>
                            <input
                              type="checkbox"
                              checked={selectedSubAssemblies.length === subAssemblesData.length && subAssemblesData.length > 0}
                              onChange={() => {
                                if (selectedSubAssemblies.length === subAssemblesData.length) {
                                  setSelectedSubAssemblies([]);
                                } else {
                                  const allSubAssemblies = subAssemblesData.map((item, index) => ({ ...item, index }));
                                  setSelectedSubAssemblies(allSubAssemblies);
                                }
                              }}
                            />
                          </th>
                          <th>Sub Model ID</th>
                          <th>Engine Model</th>
                        </tr>
                      </thead>
                      <tbody>
                        {subAssemblesData.map((item, index) => (
                          <tr key={index}>
                            <td>
                              <input
                                type="checkbox"
                                checked={selectedSubAssemblies.some(selected => selected.index === index)}
                                onChange={() => handleSubAssemblyRowClick(item, index)}
                              />
                            </td>
                            <td>{item.subModelId || "N/A"}</td>
                            <td>{item.isEngineModel ? "Yes" : "No"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}

            <div className="modalActions">
              <button 
                onClick={handleDeleteSubAssemblies}
                disabled={selectedSubAssemblies.length === 0 || deleteSubAssembliesLoading || subAssemblesLoading}
                style={{ 
                  backgroundColor: selectedSubAssemblies.length === 0 ? '#ccc' : '#dc3545', 
                  color: 'white',
                  marginRight: '8px'
                }}
              >
                {deleteSubAssembliesLoading ? (
                  <>
                    <div className="button-spinner" style={{ display: 'inline-block', marginRight: '8px' }}></div>
                    Removing...
                  </>
                ) : (
                  "Remove"
                )}
              </button>
              <button onClick={handleCloseSubAssemblesModal}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {isAddSubassemblyModalOpen && selectedRows.length === 1 && (
        <div className="modalOverlay">
          <div className="modalContent" style={{ minWidth: "400px", maxWidth: "500px" }}>
            <h3>Add SubAssembly</h3>
            
            {addSubassemblyError && (
              <div style={{ color: "red", marginBottom: "12px", fontSize: "14px" }}>
                {addSubassemblyError}
              </div>
            )}

            <div style={{ marginBottom: "16px" }}>
              <label htmlFor="subModelId">Sub Model ID:</label>
              <input
                id="subModelId"
                type="text"
                value={addSubassemblySubModelId}
                onChange={(e) => setAddSubassemblySubModelId(e.target.value)}
                placeholder="Enter Sub Model ID"
                disabled={addSubassemblyLoading}
              />
            </div>

            <div style={{ marginBottom: "16px" }}>
              <label style={{ display: "flex", alignItems: "center", cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={addSubassemblyIsEngineModel}
                  onChange={(e) => setAddSubassemblyIsEngineModel(e.target.checked)}
                  disabled={addSubassemblyLoading}
                  style={{ marginRight: "8px", cursor: "pointer" }}
                />
                Is Engine Model
              </label>
            </div>

            <div className="modalActions">
              <button 
                onClick={handleAddSubassembly}
                disabled={addSubassemblyLoading}
              >
                {addSubassemblyLoading ? "Adding..." : "Add"}
              </button>
              <button 
                onClick={handleCloseAddSubassemblyModal}
                disabled={addSubassemblyLoading}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <AddNewModelModal
        open={isAddNewModelModalOpen}
        onClose={() => setIsAddNewModelModalOpen(false)}
        onSuccess={(message, modelNumber) => {
          setAddNewModelSuccessMessage(message);
          refreshModelListAfterAdd(modelNumber);
        }}
      />

      {isDeleteConfirmModalOpen && (
        <div className="modalOverlay">
          <div className="modalContent" style={{ minWidth: "500px", maxWidth: "600px" }}>
            <h3>Confirm Delete</h3>
            {error && <p style={{ color: "red", marginBottom: "12px" }}>{error}</p>}
            <p>
              Are you sure you want to delete {selectedRows.length} model{selectedRows.length > 1 ? 's' : ''}?
            </p>
            {selectedRows.length > 0 && (
              <div style={{ marginBottom: '16px', maxHeight: '200px', overflowY: 'auto' }}>
                <strong>Models to be deleted:</strong>
                <ul style={{ marginTop: '8px', paddingLeft: '20px' }}>
                  {selectedRows.map((row, index) => (
                    <li key={index} style={{ marginBottom: '4px' }}>
                      {row.number || 'N/A'}
                      {row.brandName ? ` (${row.brandName})` : ''}
                      {row.name ? ` - ${row.name}` : ''}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <p style={{ color: '#dc3545', fontWeight: 'bold' }}>
              This action cannot be undone.
            </p>
            <div className="modalActions">
              <button 
                onClick={handleConfirmDelete} 
                disabled={deleteLoading}
                style={{ backgroundColor: '#dc3545', color: 'white' }}
              >
                {deleteLoading ? (
                  <>
                    <div className="button-spinner" style={{ display: 'inline-block', marginRight: '8px' }}></div>
                    Deleting...
                  </>
                ) : (
                  "Delete"
                )}
              </button>
              <button 
                onClick={handleCancelDelete} 
                disabled={deleteLoading}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {isUpdateDescriptionModalOpen && selectedRows.length === 1 && (
        <div className="modalOverlay">
          <div className="modalContent" style={{ minWidth: "400px", maxWidth: "500px" }}>
            <h3>Update Description</h3>
            
            {updateDescriptionError && (
              <div style={{ color: "red", marginBottom: "12px", fontSize: "14px" }}>
                {updateDescriptionError}
              </div>
            )}

            <div style={{ marginBottom: "16px" }}>
              <label htmlFor="modelNumber">Model Number:</label>
              <input
                id="modelNumber"
                type="text"
                value={selectedRows[0]?.number || ""}
                disabled={true}
                style={{ opacity: 0.7, cursor: "not-allowed", background: "#f5f5f5" }}
              />
            </div>

            <div style={{ marginBottom: "16px" }}>
              <label htmlFor="description">Description:</label>
              <input
                id="description"
                type="text"
                value={updateDescriptionName}
                onChange={(e) => setUpdateDescriptionName(e.target.value)}
                placeholder="Enter Description"
                disabled={updateDescriptionLoading}
              />
            </div>

            <div className="modalActions">
              <button 
                onClick={handleUpdateDescription}
                disabled={updateDescriptionLoading}
              >
                {updateDescriptionLoading ? "Updating..." : "Update"}
              </button>
              <button 
                onClick={handleCloseUpdateDescriptionModal}
                disabled={updateDescriptionLoading}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {isUpdateProductModalOpen && selectedRows.length === 1 && (
          <div className="modalOverlay" onClick={handleCloseUpdateProductModal}>
          <div className="modalContent" onClick={(e) => e.stopPropagation()} style={{ minWidth: "500px", maxWidth: "600px" }}>
            <h3>Update Product</h3>
            
              {updateProductError && (
              <div style={{ color: "red", marginBottom: "12px", fontSize: "14px" }}>
                {updateProductError}
              </div>
            )}

            <div style={{ marginBottom: "16px" }}>
              <label htmlFor="productType">Product Type:</label>
              {productTypesLoading ? (
                <div style={{ padding: "10px", textAlign: "center" }}>
                  <div className="button-spinner" style={{ display: 'inline-block', marginRight: '8px' }}></div>
                  Loading product types...
                </div>
              ) : (
                <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                  <select
                    id="productType"
                    value={selectedProductTypeId}
                    onChange={handleProductTypeChange}
                    disabled={updateProductLoading}
                    style={{
                      flex: "1",
                      padding: "8px 10px",
                      fontSize: "14px",
                      border: "2px solid #007bff",
                      borderRadius: "4px",
                      boxSizing: "border-box"
                    }}
                  >
                    <option value="">Select a Product Type</option>
                    {productTypes.map((productType) => (
                      <option key={productType.id} value={productType.id}>
                        {[productType.name, productType.id].filter(Boolean).join(" - ")}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            {(() => {
              const modelId = selectedRows[0]?.id || "";
              const brandPart = modelId.length >= 7 ? modelId.substring(7, 11) : "";
              const modelNumberPart = modelId.length > 11 ? modelId.substring(11) : "";
              return (
                <>
                  <div style={{ marginBottom: "16px" }}>
                    <label>Brand:</label>
                    <div
                      style={{
                        width: "100%",
                        padding: "8px 10px",
                        fontSize: "14px",
                        border: "1px solid #ccc",
                        borderRadius: "4px",
                        boxSizing: "border-box",
                        backgroundColor: "#e9ecef",
                        color: "#6c757d",
                        cursor: "not-allowed"
                      }}
                    >
                      {brandPart || "—"}
                    </div>
                  </div>
                  <div style={{ marginBottom: "16px" }}>
                    <label>Model Number:</label>
                    <div
                      style={{
                        width: "100%",
                        padding: "8px 10px",
                        fontSize: "14px",
                        border: "1px solid #ccc",
                        borderRadius: "4px",
                        boxSizing: "border-box",
                        backgroundColor: "#e9ecef",
                        color: "#6c757d",
                        cursor: "not-allowed"
                      }}
                    >
                      {modelNumberPart || "—"}
                    </div>
                  </div>
                </>
              );
            })()}

            <div className="modalActions">
              <button 
                onClick={handleUpdateProduct}
                disabled={updateProductLoading || !selectedProductTypeId || productTypesLoading || selectedProductTypeId === originalProductTypeId}
                style={{ backgroundColor: '#007bff', color: 'white' }}
              >
                {updateProductLoading ? (
                  <>
                    <div className="button-spinner" style={{ display: 'inline-block', marginRight: '8px' }}></div>
                    Updating...
                  </>
                ) : (
                  "Update"
                )}
              </button>
              <button 
                onClick={handleCloseUpdateProductModal}
                disabled={updateProductLoading}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
